/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { getRem } from 'lib/core';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { PageSizeMenu } from '../blocks';
import { ROWS_PER_PAGE_OPTIONS } from '../constants';
import { PageLabel, RowLabel, PageNavigationItems } from '../elements';

const StyledTablePagination = styled.div`
  align-items: center;
  background-color: ${({ theme }) => theme.color.additional.light.value};
  display: flex;
  height: ${getRem('36px')};
  justify-content: flex-end;
  padding: ${getRem('6px')} ${({ theme }) => theme.size.spacing.medium.value};
`;

const Pagination = ({
  ariaLabel = 'pagination',
  buttonLabels = {
    goToFirst: 'Go to first',
    goToLast: 'Go to last',
    goToNext: 'Go to next',
    goToPrevious: 'Go to previous',
  },
  className = '',
  dataTestId = '',
  defaultCurrentPage = 1,
  defaultPageSize = ROWS_PER_PAGE_OPTIONS[0],
  id,
  isMenuDisabled = false,
  isMobileView = false,
  menuProps = {},
  ofText = 'of',
  onSelect,
  pageSizeOptions = ROWS_PER_PAGE_OPTIONS,
  pageText = 'Page',
  resetToPageOneOnPageSizeChange = true,
  rowsPerPageLabel = 'Rows per page:',
  showGoToFirstLastPage = true,
  totalRowCount,
  ...other
}) => {
  /**
   * Use defaultPageSize, defaultCurrentPage, and totalRowCount props to calculate initial
   * startRecord, endRecord, totalPages, and startIndex values
   */
  const initialStartRecord = Math.min(Math.max((defaultCurrentPage - 1) * defaultPageSize.value + 1, 1), totalRowCount);
  const initialEndRecord = Math.min(defaultCurrentPage * defaultPageSize.value, totalRowCount);
  const initialTotalPages = Math.ceil(totalRowCount / defaultPageSize.value);

  const [internalState, setInternalState] = useState({
    startIndex: initialStartRecord - 1,
    endIndex: initialStartRecord + defaultPageSize,
    pageSize: defaultPageSize,
    currentPage: defaultCurrentPage,
    startRecord: initialStartRecord,
    endRecord: initialEndRecord,
    totalPages: initialTotalPages,
  });

  const rowsSummaryText = `${!totalRowCount ? '0' : internalState.startRecord} - ${
    !totalRowCount ? '0' : internalState.endRecord
  } ${ofText} ${!totalRowCount ? '0' : totalRowCount}`;

  const handlePageSize = (option) => {
    const newPageSizeValue = option.value;
    const newPageSizeObj = option;
    if (newPageSizeValue === internalState.pageSize.value) {
      return;
    }
    const newStartIndex = resetToPageOneOnPageSizeChange
      ? 0
      : Math.floor(internalState.startIndex / newPageSizeValue) * newPageSizeValue;
    const newTotalPages = Math.ceil(totalRowCount / newPageSizeValue);
    const newCurrentPage = Math.floor(newStartIndex / newPageSizeValue) + 1;
    const newEndIndex = Math.min(newStartIndex + newPageSizeValue - 1, totalRowCount - 1);

    setInternalState((previousState) => ({
      ...previousState,
      pageSize: newPageSizeObj,
      startIndex: newStartIndex,
      endIndex: newEndIndex,
      currentPage: newCurrentPage,
      startRecord: newStartIndex + 1,
      endRecord: Math.min(newEndIndex + 1, totalRowCount),
      totalPages: newTotalPages,
    }));

    onSelect(newStartIndex, newEndIndex, newPageSizeObj.value, newCurrentPage);
  };

  const handlePagination = (page = 1) => {
    // calculate everything
    const newStartIndex = (page - 1) * internalState.pageSize.value;
    const newEndIndex = Math.min(newStartIndex + internalState.pageSize.value - 1, totalRowCount - 1);
    const newCurrentPage = page;
    const newTotalPages = Math.ceil(totalRowCount / internalState.pageSize.value);

    // update internal state and call onSelect with the same values
    setInternalState((previousState) => ({
      ...previousState,
      startIndex: newStartIndex,
      endIndex: Math.min(newEndIndex, totalRowCount - 1),
      currentPage: newCurrentPage,
      startRecord: newStartIndex + 1,
      endRecord: Math.min(newEndIndex + 1, totalRowCount),
      totalPages: newTotalPages,
    }));
    onSelect(newStartIndex, newEndIndex, internalState.pageSize.value, newCurrentPage);
  };

  useEffect(() => {
    handlePagination(defaultCurrentPage);
  }, [defaultCurrentPage, totalRowCount]);

  return (
    <StyledTablePagination
      className={className}
      data-testid={dataTestId ? `${dataTestId}-pagination` : undefined}
      {...other}
    >
      {!isMobileView && (
        <>
          <PageLabel
            className={className ? `${className}-page-label` : undefined}
            id={id}
            text={rowsPerPageLabel}
            data-testid={dataTestId ? `${dataTestId}-page-label` : undefined}
          />
          <PageSizeMenu
            className={className ? `${className}-page-size-menu` : undefined}
            dataTestId={dataTestId ? `${dataTestId}-page-size-menu` : undefined}
            isDisabled={isMenuDisabled}
            dropDownOptions={pageSizeOptions}
            pageSize={internalState.pageSize.label}
            onSelect={handlePageSize}
            {...menuProps}
          />
          <RowLabel
            text={rowsSummaryText}
            className={className ? `${className}-row-label` : undefined}
            data-testid={dataTestId ? `${dataTestId}-row-label` : undefined}
          />
        </>
      )}

      <PageNavigationItems
        currentPage={internalState.currentPage}
        onSelect={handlePagination}
        pageSize={internalState.pageSize}
        totalPages={internalState.totalPages}
        {...{
          ariaLabel,
          buttonLabels,
          className,
          dataTestId,
          id,
          ofText,
          pageText,
          showGoToFirstLastPage,
          totalRowCount,
        }}
      />
    </StyledTablePagination>
  );
};

Pagination.propTypes = {
  /** Informs screen reader users what actions they should take */
  ariaLabel: PropTypes.string,
  /** Sets of pagination button labels */
  buttonLabels: PropTypes.shape({
    /** Label for "go to first page" button */
    goToFirst: PropTypes.node,
    /** Label for "go to last page" button */
    goToLast: PropTypes.node,
    /** Label for "go to next page" button */
    goToNext: PropTypes.node,
    /** Label for "go to previous page" button */
    goToPrevious: PropTypes.node,
  }),
  /** Adds new class to Pagination */
  className: PropTypes.string,
  /** The application controlled current page */
  defaultCurrentPage: PropTypes.number,
  /** The number of rows per page; overrides default of ROWS_PER_PAGE_OPTIONS[0] (which by default is 10) */
  defaultPageSize: PropTypes.shape({
    /** Label of the dropdown option */
    label: PropTypes.string,
    /** Value of the dropdown option */
    value: PropTypes.number,
  }),
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Value of total number of rows or items in table */
  totalRowCount: PropTypes.number.isRequired,
  /** Id value used as unique identifier */
  id: PropTypes.string.isRequired,
  /** Set 'of' text on pagination label */
  ofText: PropTypes.string,
  /** Callback function that is triggered when page size count is changed or when paginating */
  onSelect: PropTypes.func.isRequired,
  /** Sets label for rows per page dropdown */
  pageSizeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      /** Label of the dropdown option */
      label: PropTypes.string,
      /** Value of the dropdown option */
      value: PropTypes.number,
    })
  ),
  /** Set 'Page' text on pagination label */
  pageText: PropTypes.string,
  /** If false, keeps viewable rows showing (default true) */
  resetToPageOneOnPageSizeChange: PropTypes.bool,
  /** List of available rows or items per page */
  rowsPerPageLabel: PropTypes.string,
  /** If true, go to first/last page buttons are shown */
  showGoToFirstLastPage: PropTypes.bool,
  /** Disables the rows per page dropdown menu */
  isMenuDisabled: PropTypes.bool,
  /** isMobileView is used to change view for mobile */
  isMobileView: PropTypes.bool,
  /** any other menu props */
  menuProps: PropTypes.shape({}),
};

export { Pagination };
